import { Badge } from '@ipfabric/ui-components/Badge/Badge';
import { Tabs } from '@ipfabric/ui-components/Tabs/Tabs';
import type { FormSnippetGroup } from '@ipfabric/ui-components/utils/formSnippets/FormSnippetGroup';
import type { FormCompositionHook } from '@ipfabric/ui-components/utils/formSnippets/useFormComposition';
import { safeAbsurd } from '@ipfabric/utils';
import { useWatch } from 'react-hook-form';
import type { ApplicationMappingSubTab } from '../../../composition/DiscoverySettings/DiscoverySettings';
import type { DiscoverySettingsObject } from '../../../model/DiscoverySettingsObject';
import type {
  AimApplicationDraft,
  AimFlowDraft,
  AimIntegrationDraft,
  AimWorkloadDraft,
} from '../../../model/aimDrafts';
import type { storedAimApplications } from '../../../snippets/storedAimApplications';
import type { storedAimFlows } from '../../../snippets/storedAimFlows';
import type { storedAimIntegrations } from '../../../snippets/storedAimIntegrations';
import type { storedAimWorkloads } from '../../../snippets/storedAimWorkloads';
import { AimApplicationsSubTab } from './AimApplicationsSubTab';
import { AimFlowsSubTab } from './AimFlowsSubTab';
import { AimIntegrationsSubTab } from './AimIntegrationsSubTab';
import { AimWorkloadsSubTab } from './AimWorkloadsSubTab';

type Props = {
  formComposition: FormCompositionHook<
    'aimApplications' | 'aimWorkloads' | 'aimFlows' | 'aimIntegrations',
    {
      aimIntegrations: FormSnippetGroup<
        DiscoverySettingsObject,
        'aimIntegrations',
        unknown,
        { aimIntegrations: typeof storedAimIntegrations }
      >;
      aimApplications: FormSnippetGroup<
        DiscoverySettingsObject,
        'aimApplications',
        unknown,
        { aimApplications: typeof storedAimApplications }
      >;
      aimWorkloads: FormSnippetGroup<
        DiscoverySettingsObject,
        'aimWorkloads',
        unknown,
        { aimWorkloads: typeof storedAimWorkloads }
      >;
      aimFlows: FormSnippetGroup<
        DiscoverySettingsObject,
        'aimFlows',
        unknown,
        { aimFlows: typeof storedAimFlows }
      >;
    }
  >;
  subTab: ApplicationMappingSubTab;
  setSubTab: (subTab: ApplicationMappingSubTab) => void;
};

export const ApplicationMappingTab = ({
  formComposition,
  subTab,
  setSubTab,
}: Props) => {
  const applications: AimApplicationDraft[] = useWatch({
    control: formComposition.form.control,
    name: 'aimApplications',
  });
  const workloads: AimWorkloadDraft[] = useWatch({
    control: formComposition.form.control,
    name: 'aimWorkloads',
  });
  const flows: AimFlowDraft[] = useWatch({
    control: formComposition.form.control,
    name: 'aimFlows',
  });
  const integrations: AimIntegrationDraft[] = useWatch({
    control: formComposition.form.control,
    name: 'aimIntegrations',
  });

  const tabs = [
    {
      id: 'aimIntegrations' as const,
      label: 'Integrations',
      badge: (
        <Badge color='neutral' variant='plain'>
          {integrations.length}
        </Badge>
      ),
    },
    {
      id: 'aimApplications' as const,
      label: 'Applications',
      badge: (
        <Badge color='neutral' variant='plain'>
          {applications.length}
        </Badge>
      ),
    },
    {
      id: 'aimWorkloads' as const,
      label: 'Workloads',
      badge: (
        <Badge color='neutral' variant='plain'>
          {workloads.length}
        </Badge>
      ),
    },
    {
      id: 'aimFlows' as const,
      label: 'Flows',
      badge: (
        <Badge color='neutral' variant='plain'>
          {flows.length}
        </Badge>
      ),
    },
  ];

  return (
    <div className='flex flex-col flex-1 drop-shadow'>
      <Tabs tabs={tabs} value={subTab} onChange={setSubTab} variant='outer' />
      <div className='flex flex-1 w-full'>
        {(() => {
          switch (subTab) {
            case 'aimApplications':
              return (
                <AimApplicationsSubTab formComposition={formComposition} />
              );
            case 'aimFlows':
              return <AimFlowsSubTab formComposition={formComposition} />;
            case 'aimWorkloads':
              return <AimWorkloadsSubTab formComposition={formComposition} />;
            case 'aimIntegrations':
              return (
                <AimIntegrationsSubTab formComposition={formComposition} />
              );
            default:
              safeAbsurd(subTab, 'Unknown AIM subtab');
              return <p>Error: unknown subtab</p>;
          }
        })()}
      </div>
    </div>
  );
};
